/**
 * MenuPagePhotoGrid
 * ─────────────────
 * Multi-column photo grid /menu view used by tenants that flip
 * `business.menu_layout = "photo-grid"`. Each item is a card with an
 * image up top and description / price / Add button below — best for
 * cafés and casual dining where the photo *is* the menu.
 *
 *   - Time-slot tabs at the top so customers can preview Breakfast /
 *     Lunch / Dinner from anywhere
 *   - Categories are visible by default (no collapsing)
 *   - Multi-size items use a dropdown
 *   - Items with add-ons open the existing AddonDialog
 */
import { useState, useMemo } from "react";
import { Plus, LayoutGrid, List as ListIcon } from "lucide-react";
import { money } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { useCart } from "@/context/CartContext";
import { useMenuData } from "@/lib/useMenuData";
import MenuSlotTabs from "@/components/MenuSlotTabs";
import MenuItemDetailModal from "@/components/MenuItemDetailModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AddonDialog from "@/components/AddonDialog";
import {
  useDietaryConflictMap, AllergenBadge, BlockedAddButton, conflictWrapperClass,
} from "@/lib/useDietaryConflictMap";
import RestaurantAiChatWidget from "@/components/RestaurantAiChatWidget";

const SIZE_LABELS = {
  sml: "Small", small: "Small", reg: "Regular", regular: "Regular",
  med: "Medium", medium: "Medium", lrg: "Large", large: "Large",
  cup: "Cup (dine in)", pot: "Pot", mug: "Mug (dine in)",
  "6oz":  "6oz takeaway",  "12oz": "12oz takeaway",  "16oz": "16oz takeaway",
  "14oz": "14oz takeaway", "20oz": "20oz takeaway",
  "glass-r": "Regular glass (dine in)", "glass-l": "Large glass (dine in)",
  light: "Light", full: "Full",
};

function sizeKeys(prices, columns) {
  const ks = Object.keys(prices || {});
  if (ks.length === 0) return [];
  if (columns && columns.length) {
    const order = new Map(columns.map((k, i) => [k, i]));
    return ks.slice().sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
  }
  return ks.slice().sort();
}

const PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 3'><rect width='4' height='3' fill='%23F3F0EA'/></svg>";

export default function MenuPagePhotoGrid() {
  const { business } = useBusiness();
  const cart = useCart();
  const { cats, itemsByCat, loading, slot, setSlot } = useMenuData();
  const _allItems = useMemo(() => Object.values(itemsByCat || {}).flat(),
                                          [itemsByCat]);
  const conflictMap = useDietaryConflictMap(_allItems);
  const [sizeChoice, setSizeChoice] = useState({});
  const [addonItem, setAddonItem] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("bella_menu_view_v1") === "list" ? "list" : "grid"; } catch (_e) { return "grid"; }
  });
  const persistView = (v) => { setView(v); try { localStorage.setItem("bella_menu_view_v1", v); } catch (_e) { /* ignore */ } };

  function quickAdd(item, sizeKey) {
    if (item.addons && item.addons.length > 0) {
      const cat = cats.find((c) => c.id === item.category_id) || null;
      setAddonItem({ ...item, _category: cat });
      return;
    }
    const price = sizeKey ? Number(item.prices?.[sizeKey]) || Number(item.price) : Number(item.price) || 0;
    cart.addLine({
      item_id: item.id,
      name: item.name,
      base_price: price,
      quantity: 1,
      selected_addons: sizeKey ? [{ name: `Size: ${SIZE_LABELS[sizeKey] || sizeKey}`, price: 0 }] : [],
      notes: "",
      calories: Number(item.calories) || 0,
      tags: item.tags || [],
      price_column: sizeKey || "",
      price_label: SIZE_LABELS[sizeKey] || "",
    });
    toast.success(`${item.name} added to cart`);
  }

  if (loading) {
    return (
      <div data-testid="menu-photogrid-loading" className="max-w-6xl mx-auto px-4 py-24 text-center text-[#5C625E]">
        Loading the menu…
      </div>
    );
  }

  return (
    <div data-testid="menu-photogrid-page" className="bg-[#FDFBF7] pb-24">
      <header className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-16 pb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5A7247] font-bold text-center">{business?.tagline || "Menu"}</p>
        <h1 className="font-serif-display text-5xl md:text-6xl text-center mt-2">Menu</h1>
        <div className="mt-8">
          <MenuSlotTabs slot={slot} onChange={setSlot} />
        </div>
        <div className="mt-5 flex justify-center" data-testid="menu-view-toggle">
          <div className="inline-flex items-center bg-white border border-[#E8E5DF] rounded-full p-1">
            <button
              type="button"
              data-testid="menu-view-grid"
              onClick={() => persistView("grid")}
              className={`h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1.5 transition-colors ${
                view === "grid" ? "bg-[#121614] text-white" : "text-[#5C625E] hover:text-[#121614]"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Photo grid
            </button>
            <button
              type="button"
              data-testid="menu-view-list"
              onClick={() => persistView("list")}
              className={`h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1.5 transition-colors ${
                view === "list" ? "bg-[#121614] text-white" : "text-[#5C625E] hover:text-[#121614]"
              }`}
            >
              <ListIcon className="h-3.5 w-3.5" strokeWidth={1.5} /> List view
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14">
        {cats.map((cat) => {
          const rows = itemsByCat[cat.id] || [];
          if (rows.length === 0) return null;
          const colKeys = (cat.price_columns || []).map((c) => c.key);
          return (
            <section key={cat.id} data-testid={`menu-cat-${cat.id}`}>
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#5A7247] font-bold">
                  {cat.kind === "beverage" ? "Drinks" : cat.kind === "wine" ? "Wine" : "Food"}
                </p>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#121614] mt-1">{cat.name}</h2>
              </div>
              <div className={view === "list"
                ? "divide-y divide-[#E8E5DF] border border-[#E8E5DF] rounded-2xl bg-white overflow-hidden"
                : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              }>
                {rows.map((it) => {
                  const keys = sizeKeys(it.prices, colKeys);
                  const sizeKey = sizeChoice[it.id] || keys[0] || "";
                  const livePrice = sizeKey
                    ? Number(it.prices?.[sizeKey]) || Number(it.price) || 0
                    : Number(it.price) || 0;
                  const conflict = conflictMap.get(it.id);

                  if (view === "list") {
                    return (
                      <div
                        key={it.id}
                        data-testid={`menu-item-${it.id}`}
                        onClick={() => setDetailItem(it)}
                        title={conflict?.reasons?.join(" · ") || ""}
                        className={`flex items-center gap-4 px-4 sm:px-6 py-3 cursor-pointer hover:bg-[#FBF8F3] transition-colors
                                      ${conflictWrapperClass(conflict)}
                                      ${conflict?.hasAllergen ? "bg-rose-50/30" : ""}`}
                      >
                        {it.image_url && (
                          <img
                            src={it.image_url}
                            alt=""
                            className="w-14 h-14 rounded-lg object-cover bg-[#F3F0EA] shrink-0"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-[#121614] truncate">{it.name}</p>
                            <AllergenBadge conflict={conflict}
                                          testId={`pg-${it.id}-allergen`} />
                          </div>
                          {it.description && (
                            <p className="text-xs text-[#5C625E] line-clamp-1 mt-0.5">{it.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="font-medium text-[#121614] tabular-nums" data-testid={`menu-price-${it.id}`}>
                            {money(livePrice)}
                          </span>
                          {conflict?.hasAllergen ? (
                            <BlockedAddButton conflict={conflict}
                                          testId={`menu-blocked-${it.id}`} />
                          ) : (
                            <Button
                              data-testid={`menu-add-${it.id}`}
                              onClick={(e) => { e.stopPropagation(); quickAdd(it, sizeKey); }}
                              size="sm"
                              className="bg-[#121614] hover:bg-black text-white rounded-full px-3 h-8 text-xs"
                            >
                              <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
                              {(it.addons || []).length > 0 ? "Customise" : "Add"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <article
                      key={it.id}
                      data-testid={`menu-item-${it.id}`}
                      onClick={() => setDetailItem(it)}
                      title={conflict?.reasons?.join(" · ") || ""}
                      className={`bg-white border border-[#E8E5DF] rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md relative
                                      ${conflictWrapperClass(conflict)}`}
                    >
                      {conflict?.hasAllergen && (
                        <div className="absolute top-3 left-3 z-10">
                          <AllergenBadge conflict={conflict}
                                  testId={`pg-card-${it.id}-allergen`}
                                  size="lg" />
                        </div>
                      )}
                      <div className="aspect-[4/3] bg-[#F3F0EA] overflow-hidden">
                        <img
                          src={it.image_url || PLACEHOLDER}
                          alt={it.name}
                          className="w-full h-full object-cover transition-transform hover:scale-[1.03]"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
                        />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <p className="font-medium text-[#121614]">{it.name}</p>
                        {it.description && (
                          <p className="text-sm text-[#5C625E] mt-1 flex-1 line-clamp-3">{it.description}</p>
                        )}
                        {Array.isArray(it.wine_pairing_ids) && it.wine_pairing_ids.length > 0 && (
                          <p
                            data-testid={`pairing-hint-${it.id}`}
                            className="mt-2 text-[10px] uppercase tracking-wider font-bold text-[#7c2d12] inline-flex items-center gap-1"
                          >
                            🍷 Pairs with {it.wine_pairing_ids.length} wine{it.wine_pairing_ids.length === 1 ? "" : "s"}
                          </p>
                        )}
                        <div className="mt-3 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                          {keys.length > 1 ? (
                            <Select
                              value={sizeKey}
                              onValueChange={(v) => setSizeChoice((s) => ({ ...s, [it.id]: v }))}
                            >
                              <SelectTrigger
                                data-testid={`menu-size-${it.id}`}
                                className="h-9 rounded-full text-xs flex-1"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {keys.map((k) => (
                                  <SelectItem
                                    key={k}
                                    value={k}
                                    data-testid={`menu-size-${it.id}-${k}`}
                                  >
                                    {SIZE_LABELS[k] || k} · {money(Number(it.prices?.[k]) || 0)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span
                              data-testid={`menu-price-${it.id}`}
                              className="font-bold tabular-nums text-[#121614]"
                            >
                              {money(livePrice)}
                            </span>
                          )}
                          <Button
                            data-testid={`menu-add-${it.id}`}
                            onClick={(e) => { e.stopPropagation(); quickAdd(it, sizeKey); }}
                            size="sm"
                            disabled={conflict?.hasAllergen}
                            className={`rounded-full px-3 h-9 ${
                              conflict?.hasAllergen
                                ? "bg-rose-200 text-rose-800 cursor-not-allowed"
                                : "bg-[#121614] hover:bg-black text-white"}`}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
                            {conflict?.hasAllergen ? "⚠ Blocked"
                              : (it.addons || []).length > 0 ? "Customise" : "Add"}
                          </Button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </main>

      {addonItem && <AddonDialog item={addonItem} onClose={() => setAddonItem(null)} />}
      <MenuItemDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAddPrimary={(it) => {
          setDetailItem(null);
          // small delay so the close animation finishes before AddonDialog opens
          setTimeout(() => {
            if (it.addons && it.addons.length > 0) {
              const cat = cats.find((c) => c.id === it.category_id) || null;
              setAddonItem({ ...it, _category: cat });
            } else {
              quickAdd(it, "");
            }
          }, 150);
        }}
      />
      <RestaurantAiChatWidget />
    </div>
  );
}
